import { Check } from "lucide-react";

type StageStatus = "done" | "active" | "upcoming";

type Stage = {
  key: string;
  label: string;
  sub: string;
  status: StageStatus;
};

// Capacity-based pricing — no public dates. Each wave releases as the previous
// one sells through. Wave name + status only (price hidden by design).
const stages: Stage[] = [
  { key: "early", label: "Early Bird", sub: "Sold Out", status: "done" },
  { key: "wave1", label: "Wave 1", sub: "Sold Out", status: "done" },
  { key: "wave2", label: "Wave 2", sub: "⚠️ Ending Soon", status: "active" },
  { key: "wave3", label: "Wave 3", sub: "Next Price Up", status: "upcoming" },
];

// Active fill stops at Wave 2 (66% along the line between dot 1 and dot 4).
const FILL_PERCENT = 66;

const PricingStageTimeline = () => {
  return (
    <section className="bg-yacht-cream py-10 md:py-14 px-6 border-y border-yacht-charcoal/10">
      <div className="max-w-3xl mx-auto">
        <p className="text-yacht-blue text-[10px] md:text-xs font-bold tracking-[0.3em] uppercase text-center mb-2">
          ★ Where We're At ★
        </p>
        <p className="text-yacht-charcoal/55 text-xs md:text-sm text-center mb-7">
          Prices rise with each wave. Lock the current wave before it sells through.
        </p>

        <div className="relative">
          {/* Background line */}
          <div className="absolute top-[10px] md:top-3 h-0.5 bg-yacht-charcoal/15 z-0" style={{ left: "12.5%", right: "12.5%" }} />

          {/* Active progress fill */}
          <div
            className="absolute top-[10px] md:top-3 h-0.5 bg-yacht-blue z-0 transition-all duration-500"
            style={{ left: "12.5%", width: `${FILL_PERCENT * 0.75}%` }}
          />

          {/* Stage dots */}
          <div className="relative grid grid-cols-4">
            {stages.map((s) => (
              <div key={s.key} className="flex flex-col items-center text-center px-1">
                <div className="relative w-5 h-5 md:w-6 md:h-6 z-10">
                  {s.status === "done" && (
                    <div className="w-full h-full rounded-full bg-yacht-blue flex items-center justify-center">
                      <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" strokeWidth={3} />
                    </div>
                  )}
                  {s.status === "active" && (
                    <>
                      <span className="absolute inset-0 rounded-full bg-yacht-yellow animate-ping opacity-50" />
                      <div className="relative w-full h-full rounded-full bg-yacht-yellow ring-[3px] md:ring-4 ring-yacht-yellow/25" />
                    </>
                  )}
                  {s.status === "upcoming" && (
                    <div className="w-full h-full rounded-full bg-yacht-cream border-2 border-yacht-charcoal/25" />
                  )}
                </div>
                <p
                  className={`mt-3 text-[10px] md:text-xs font-bold uppercase tracking-[0.15em] ${
                    s.status === "active"
                      ? "text-yacht-charcoal"
                      : s.status === "done"
                      ? "text-yacht-charcoal/65"
                      : "text-yacht-charcoal/40"
                  }`}
                >
                  {s.label}
                </p>
                <p
                  className={`mt-1.5 text-[10px] md:text-[11px] ${
                    s.status === "active"
                      ? "text-yacht-blue font-semibold"
                      : s.status === "done"
                      ? "text-yacht-charcoal/45"
                      : "text-yacht-charcoal/40"
                  }`}
                >
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingStageTimeline;
